import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Archive } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { employeeApi } from '../lib/api';
import { toast } from 'sonner';
import { useUnit } from '../contexts/UnitContext';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

export default function ExEmployees() {
  const { activeUnit } = useUnit();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeUnit]);
  const load = async () => {
    setLoading(true);
    try { const r = await employeeApi.exList({ unit: activeUnit }); setRows(r.data); }
    catch (e) { toast.error('Failed to load ex-employees'); }
    finally { setLoading(false); }
  };

  const filtered = useMemo(
    () => rows.filter((e) => !search || (e.name || '').toLowerCase().includes(search.toLowerCase())),
    [rows, search]
  );

  return (
    <div data-testid="ex-employees-page" className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Header />
        <main className="flex-1 overflow-auto p-5 sm:p-6 lg:p-8 page-enter">
          <div className="max-w-[1600px] mx-auto">
            <div className="flex items-center gap-3 mb-2">
              <Archive className="w-6 h-6 text-[#5A6377]" />
              <h1 className="font-display text-3xl sm:text-4xl tracking-[-0.035em] font-extrabold text-[#172554]">Ex-Employees</h1>
            </div>
            <p className="text-sm text-[#5A6377] mb-6">{filtered.length} former employees — records and salary history retained</p>

            <div className="mb-6">
              <Input data-testid="ex-search" placeholder="Search by name..." value={search}
                     onChange={(e) => setSearch(e.target.value)} className="max-w-xs border-[#E6E9F0]" />
            </div>

            {loading ? (
              <div className="text-center py-12"><div className="w-12 h-12 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto"></div></div>
            ) : (
              <div className="bg-white border border-[#E6E9F0] rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#102468] text-white text-xs font-semibold tracking-wide uppercase">
                      <tr>
                        <th className="py-3 px-4 text-left">Name</th>
                        <th className="py-3 px-4 text-left">Card No</th>
                        <th className="py-3 px-4 text-left">Unit</th>
                        <th className="py-3 px-4 text-left">Department</th>
                        <th className="py-3 px-4 text-left">Designation</th>
                        <th className="py-3 px-4 text-left">DOJ</th>
                        <th className="py-3 px-4 text-left">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((e) => (
                        <tr key={e.id} data-testid={`ex-row-${e.id}`} className="border-b border-[#E6E9F0] last:border-0 hover:bg-[#F7F8FB]">
                          <td className="py-3 px-4 text-sm text-[#11182B] font-medium">{e.name}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{e.card_no}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{e.unit}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{e.department}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{e.designation}{e.level ? <span className="text-[10px] text-[#98A1B2] ml-1">L{e.level}</span> : null}</td>
                          <td className="py-3 px-4 text-sm text-[#5A6377]">{e.doj}</td>
                          <td className="py-3 px-4">
                            <Link to={`/employees/${e.id}`}>
                              <Button data-testid={`view-ex-${e.id}`} variant="ghost" size="sm" className="text-[#2563EB] hover:bg-[#EFF1F6]"><Eye className="w-4 h-4" /></Button>
                            </Link>
                          </td>
                        </tr>
                      ))}
                      {filtered.length === 0 && (
                        <tr><td colSpan={7} className="py-12 text-center text-[#98A1B2]">No former employees in scope</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
